import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ReactNode } from 'react';
import type { Column } from './DataTable';

export interface SortableGroup<T> {
  id: string;
  /** Names the group for its drag handle and screen-reader announcements. */
  name: string;
  /** Content of the heading row above the group's rows; omit for a plain list. */
  label?: ReactNode;
  rows: T[];
  /** Shown in place of rows when the group is empty. */
  empty?: ReactNode;
}

/** Announcements that name items and give positions ("Picked up Armbar, position 2 of 5"). */
function announcementsFor(ids: string[], names: Map<string, string>): Announcements {
  const name = (id: UniqueIdentifier) => names.get(String(id)) ?? 'Item';
  const position = (id: UniqueIdentifier) => ids.indexOf(String(id)) + 1;
  return {
    onDragStart: ({ active }) =>
      `Picked up ${name(active.id)}, position ${position(active.id)} of ${ids.length}.`,
    onDragOver: ({ active, over }) =>
      over
        ? `${name(active.id)} moved to position ${position(over.id)} of ${ids.length}.`
        : undefined,
    onDragEnd: ({ active, over }) =>
      over
        ? `${name(active.id)} dropped at position ${position(over.id)} of ${ids.length}.`
        : `${name(active.id)} dropped.`,
    onDragCancel: ({ active }) => `Moving ${name(active.id)} was cancelled.`,
  };
}

/** Calls `onReorder` with the complete new order when an item is dropped somewhere new. */
const dropHandler =
  (ids: string[], onReorder: (ids: string[]) => void) =>
  ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from !== -1 && to !== -1) onReorder(arrayMove(ids, from, to));
  };

function useOrderingSensors() {
  return useSensors(
    // A small distance keeps a plain click on the handle from starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
}

/**
 * A table of groups (e.g. categories) whose rows are reordered by dragging the handle, or from the
 * keyboard: focus the handle, press Space, move with the arrow keys and press Space again (Escape
 * cancels). Rows only move within their own group. With `onReorderGroups`, whole groups can be
 * moved the same way by the handle in their heading.
 */
export function SortableTable<T>({
  columns,
  groups,
  rowKey,
  rowLabel,
  onReorder,
  onReorderGroups,
  empty = 'Nothing here yet.',
}: {
  columns: Column<T>[];
  groups: SortableGroup<T>[] | undefined;
  rowKey: (row: T) => string;
  /** Names the row for the drag handle and screen-reader announcements. */
  rowLabel: (row: T) => string;
  onReorder: (groupId: string, ids: string[]) => void;
  onReorderGroups?: (ids: string[]) => void;
  empty?: ReactNode;
}) {
  const sensors = useOrderingSensors();
  if (!groups) return <div className="empty">Loading…</div>;
  const width = columns.length + 1;
  const groupIds = groups.map((g) => g.id);
  return (
    <div className="table-wrap responsive sortable">
      <table>
        <thead>
          <tr>
            <th style={{ width: 44 }} scope="col">
              <span className="visually-hidden">Reorder</span>
            </th>
            {columns.map((c) => (
              <th key={c.key} style={c.width ? { width: c.width } : undefined} scope="col">
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        {groups.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={width} className="empty">
                {empty}
              </td>
            </tr>
          </tbody>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={dropHandler(groupIds, (ids) => onReorderGroups?.(ids))}
            // The screen-reader text is a <div>, which may not sit inside a <table>.
            accessibility={{
              announcements: announcementsFor(groupIds, new Map(groups.map((g) => [g.id, g.name]))),
              container: document.body,
            }}
          >
            <SortableContext items={groupIds} strategy={verticalListSortingStrategy}>
              {groups.map((g) => (
                <SortableBody
                  key={g.id}
                  group={g}
                  movable={Boolean(onReorderGroups)}
                  columns={columns}
                  rowKey={rowKey}
                  rowLabel={rowLabel}
                  width={width}
                  onReorder={(ids) => onReorder(g.id, ids)}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </table>
    </div>
  );
}

function SortableBody<T>({
  group,
  movable,
  columns,
  rowKey,
  rowLabel,
  width,
  onReorder,
}: {
  group: SortableGroup<T>;
  movable: boolean;
  columns: Column<T>[];
  rowKey: (row: T) => string;
  rowLabel: (row: T) => string;
  width: number;
  onReorder: (ids: string[]) => void;
}) {
  const sensors = useOrderingSensors();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id, disabled: !movable });
  const ids = group.rows.map(rowKey);
  return (
    <tbody
      ref={setNodeRef}
      className={isDragging ? 'dragging' : undefined}
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      {group.label !== undefined || movable ? (
        <tr className="group-row">
          <th colSpan={width} scope="colgroup">
            <div className="group-heading">
              {movable ? (
                <DragHandle
                  label={`Move category ${group.name}`}
                  handleRef={setActivatorNodeRef}
                  attributes={attributes}
                  listeners={listeners}
                />
              ) : null}
              {group.label}
            </div>
          </th>
        </tr>
      ) : null}
      {/* Rows have their own drag context, so they only move within this group. */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={dropHandler(ids, onReorder)}
        accessibility={{
          announcements: announcementsFor(
            ids,
            new Map(group.rows.map((r) => [rowKey(r), rowLabel(r)])),
          ),
          container: document.body,
        }}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {group.rows.length === 0 ? (
            <tr>
              <td colSpan={width} className="empty">
                {group.empty ?? 'Nothing here yet.'}
              </td>
            </tr>
          ) : (
            group.rows.map((row) => (
              <SortableRow
                key={rowKey(row)}
                id={rowKey(row)}
                label={rowLabel(row)}
                cells={columns.map((c) => (
                  <td
                    key={c.key}
                    data-label={c.label ?? (typeof c.header === 'string' ? c.header : undefined)}
                  >
                    {c.render(row)}
                  </td>
                ))}
              />
            ))
          )}
        </SortableContext>
      </DndContext>
    </tbody>
  );
}

function SortableRow({ id, label, cells }: { id: string; label: string; cells: ReactNode }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  return (
    <tr
      ref={setNodeRef}
      className={isDragging ? 'dragging' : undefined}
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      <td className="drag-cell">
        <DragHandle
          label={`Move ${label}`}
          handleRef={setActivatorNodeRef}
          attributes={attributes}
          listeners={listeners}
        />
      </td>
      {cells}
    </tr>
  );
}

function DragHandle({
  label,
  handleRef,
  attributes,
  listeners,
}: {
  label: string;
  handleRef: (element: HTMLElement | null) => void;
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
}) {
  return (
    <button
      type="button"
      className="drag-handle"
      ref={handleRef}
      aria-label={label}
      {...attributes}
      {...listeners}
    >
      <svg viewBox="0 0 10 16" width="10" height="16" aria-hidden="true">
        {[3, 8, 13].map((y) =>
          [2, 8].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.5" />),
        )}
      </svg>
    </button>
  );
}
